import React, { useEffect, useRef, useState } from "react";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Upload, ScanLine, AlertTriangle, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function FoodScanner() {
  const { t, lang } = useLang();
  const [text, setText] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const { data } = await api.get("/food/history");
    setHistory(data.scans || []);
  };
  useEffect(() => { load(); }, []);

  const scanText = async () => {
    if (!text.trim()) return;
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.post("/food/scan-text", { ingredients_text: text, lang });
      setResult(data);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setScanning(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setScanning(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("lang", lang);
      const { data } = await api.post("/food/scan-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="scan-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A5D23] font-bold">Gracely OCR</p>
        <h1 className="font-serif-display text-4xl text-stone-900 mt-2">{t("food_title")}</h1>
        <p className="text-stone-500 mt-1">{lang === "id" ? "Foto label makanan atau tempel daftar bahannya. Gracely akan menandai yang tidak cocok untukmu." : "Snap a label or paste ingredients. Gracely will flag what doesn't fit you."}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-soft p-6">
          <Tabs defaultValue="text">
            <TabsList className="bg-stone-100 rounded-full">
              <TabsTrigger value="text" className="rounded-full data-[state=active]:bg-white" data-testid="tab-text">{t("tab_text")}</TabsTrigger>
              <TabsTrigger value="image" className="rounded-full data-[state=active]:bg-white" data-testid="tab-image">{t("tab_image")}</TabsTrigger>
            </TabsList>
            <TabsContent value="text" className="mt-4">
              <Textarea
                rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="water, sugar, high-fructose corn syrup, sodium nitrate, natural flavors…"
                className="rounded-xl bg-stone-50 border-transparent focus-visible:ring-2 focus-visible:ring-[#4A5D23]/30"
                data-testid="scan-text-input"
              />
              <Button onClick={scanText} disabled={scanning || !text.trim()} className="mt-4 rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white btn-pop" data-testid="scan-text-btn">
                <ScanLine className="w-4 h-4 mr-1" /> {scanning ? t("scanning") : t("scan_btn")}
              </Button>
            </TabsContent>
            <TabsContent value="image" className="mt-4">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="scan-image-input" />
              <button
                onClick={() => fileRef.current?.click()}
                className={`w-full border-2 border-dashed border-stone-300 rounded-2xl p-10 text-center hover:border-[#4A5D23] transition-colors ${scanning ? "scan-animate" : ""}`}
                data-testid="scan-image-btn"
              >
                {preview ? (
                  <img src={preview} alt="preview" className="max-h-64 mx-auto rounded-xl" />
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-stone-400" />
                    <p className="mt-3 text-stone-600 text-sm">{lang === "id" ? "Klik untuk unggah label (JPG/PNG/WEBP)" : "Click to upload label (JPG/PNG/WEBP)"}</p>
                  </>
                )}
              </button>
              {scanning && <p className="mt-3 text-sm text-stone-500 text-center">{t("scanning")}</p>}
            </TabsContent>
          </Tabs>
        </div>

        <div className="card-soft p-6">
          <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{lang === "id" ? "Hasil analisis" : "Analysis result"}</p>
          {!result && <p className="text-stone-500 text-sm mt-4">{lang === "id" ? "Belum ada analisis." : "No analysis yet."}</p>}
          {result && (
            <div className="mt-4 space-y-4" data-testid="scan-result">
              <VerdictBanner verdict={result.analysis?.verdict} t={t} />
              {result.analysis?.warnings?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">{lang === "id" ? "Bahan bermasalah" : "Concerning ingredients"}</p>
                  <ul className="space-y-2">
                    {result.analysis.warnings.map((w, i) => (
                      <li key={i} className="p-3 rounded-lg bg-[#FFEDE7] text-sm">
                        <p className="font-bold text-[#8A3A25] capitalize">{w.ingredient}</p>
                        <p className="text-stone-700">{w.reason}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.analysis?.profile_alerts?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">{lang === "id" ? "Terkait profilmu" : "Profile alerts"}</p>
                  <ul className="space-y-2">
                    {result.analysis.profile_alerts.map((w, i) => (
                      <li key={i} className="p-3 rounded-lg bg-[#FDF3D9] text-sm">
                        <p className="text-stone-800">{w.note}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.ai_summary && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">{lang === "id" ? "Ringkasan Gracely" : "Gracely's summary"}</p>
                  <div className="p-4 rounded-xl bg-[#EAF0E4] text-stone-800 whitespace-pre-wrap text-sm leading-relaxed">{result.ai_summary}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div>
          <h2 className="font-serif-display text-2xl text-stone-900">{lang === "id" ? "Riwayat" : "History"}</h2>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {history.slice(0, 6).map((s) => (
              <div key={s.id} className="card-soft p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-widest text-stone-500 font-bold capitalize">{s.source}</p>
                  <VerdictPill verdict={s.analysis?.verdict} t={t} />
                </div>
                <p className="mt-2 text-sm text-stone-700 line-clamp-3">{s.raw_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VerdictBanner({ verdict, t }) {
  const map = {
    safe: { icon: ShieldCheck, bg: "bg-[#EAF0E4]", color: "text-[#4A5D23]", label: t("verdict_safe") },
    caution: { icon: AlertTriangle, bg: "bg-[#FDF3D9]", color: "text-[#8A6B00]", label: t("verdict_caution") },
    danger: { icon: ShieldAlert, bg: "bg-[#FFEDE7]", color: "text-[#E07A5F]", label: t("verdict_danger") },
  };
  const v = map[verdict] || map.safe;
  const Icon = v.icon;
  return (
    <div className={`flex items-center gap-3 p-4 rounded-xl ${v.bg}`} data-testid="verdict-banner">
      <Icon className={`w-6 h-6 ${v.color}`} />
      <p className={`font-bold ${v.color}`}>{v.label}</p>
    </div>
  );
}

function VerdictPill({ verdict, t }) {
  const map = {
    safe: { bg: "bg-[#EAF0E4]", color: "text-[#4A5D23]", label: t("verdict_safe") },
    caution: { bg: "bg-[#FDF3D9]", color: "text-[#8A6B00]", label: t("verdict_caution") },
    danger: { bg: "bg-[#FFEDE7]", color: "text-[#E07A5F]", label: t("verdict_danger") },
  };
  const v = map[verdict] || map.safe;
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full ${v.bg} ${v.color}`}>{v.label}</span>;
}
